import React, { useState } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useProgress } from '../contexts/ProgressContext';

const UserMenu = ({ className = '' }) => {
  const { logout, isAuthenticated, user } = useKindeAuth();
  const { setUserName, progress } = useProgress();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');

  if (!isAuthenticated) return null;

  const displayName = user?.given_name || user?.givenName || progress.userName || user?.email?.split('@')[0] || 'User';
  const email = user?.email || user?.preferred_email || '';
  const avatarInitial = (displayName || 'U').charAt(0).toUpperCase();

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      localStorage.removeItem('smc_30day_progress');
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
      setIsLoggingOut(false);
    }
  };

  const handleSaveName = () => {
    const trimmed = nameInput.trim();
    if (trimmed) {
      setUserName(trimmed);
      setIsEditingName(false);
    }
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* User avatar + info */}
      <div className="flex items-center gap-2.5 bg-gray-800/80 border border-gray-700 rounded-full pl-1.5 pr-4 py-1.5 shadow-md">
        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-teal to-accent flex items-center justify-center text-white font-bold text-sm shrink-0">
          {avatarInitial}
        </div>
        <div className="leading-tight hidden sm:block">
          <div className="text-white text-sm font-semibold max-w-[140px] truncate">{displayName}</div>
          <div className="text-gray-400 text-xs max-w-[160px] truncate">{email}</div>
        </div>
      </div>

      {/* Change Name button */}
      <button
        onClick={() => { setNameInput(progress.userName || displayName); setIsEditingName(true); }}
        className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white text-sm font-medium transition-colors"
        title="Change the name shown on your certificate"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <span className="hidden md:inline ml-1">Edit Name</span>
      </button>

      {/* Logout button */}
      <button
        onClick={handleLogout}
        disabled={isLoggingOut}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600/90 hover:bg-red-500 text-white font-semibold text-sm transition-all duration-200 shadow-md hover:shadow-lg active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
        {isLoggingOut ? 'Signing out...' : 'Sign Out'}
      </button>

      {/* Name edit modal */}
      {isEditingName && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setIsEditingName(false)}
        >
          <div
            className="relative w-full max-w-md bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-white mb-2">Edit Display Name</h3>
            <p className="text-gray-400 text-sm mb-6">
              This name will be shown on your course certificate.
            </p>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="Enter your full name"
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal mb-6"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setIsEditingName(false)}
                className="flex-1 py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveName}
                className="flex-1 py-3 bg-gradient-to-r from-teal to-accent text-white font-semibold rounded-lg hover:shadow-lg transition-all"
              >
                Save Name
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
